import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Kbd } from '@heroui/react';
import { ItemCard, PressableFeedback } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type ActionSearchBarProps = {
    /** Everything searchable, roadmap rows included — the palette shows both. */
    totalCount: number;
    onOpen: () => void;
};

/**
 * The page's opening line, and the fastest route to any of the actions.
 *
 * A full-width bar at the top of the content rather than an icon in the chrome:
 * search is the shortcut this page is built around, and it is the reason the
 * catalog below can stay quiet — one muted list instead of a wall of badges and
 * colour.
 *
 * It is a **button**, not an input, and the placeholder is an
 * `ItemCard.Description` for that reason: nothing here is typed into. Pressing
 * it opens the palette (`Ctrl/⌘ K` does the same from anywhere on the page),
 * and the palette owns the real field; a second input here would give the page
 * two carets and two result lists to keep in step.
 */
export const ActionSearchBar: React.FC<ActionSearchBarProps> = ({
    totalCount,
    onOpen,
}) => {
    const { t } = useTranslation('pages');
    // The modifier's name is shell copy, not page copy — it already exists in
    // the `components` namespace and is the same word on every screen.
    const { t: ctrl } = useTranslation('components');

    return (
        <ItemCard<'button'>
            className="relative w-full cursor-pointer overflow-hidden"
            onClick={onOpen}
            render={(props) => (
                <button
                    aria-keyshortcuts="Control+K Meta+K"
                    type="button"
                    {...props}
                />
            )}
            {...getNodeTestIdProp('control-center-search')}
        >
            <PressableFeedback.Highlight />
            <ItemCard.Icon>
                <Icon
                    aria-hidden
                    className="size-4"
                    icon="solar:magnifer-linear"
                />
            </ItemCard.Icon>
            <ItemCard.Content className="min-w-0">
                {/* Two lines, one shown at a time. The examples are what teach
                    the field what it accepts, so they are worth the width where
                    there is width; at 390 they truncate mid-word, and a
                    truncated hint teaches nothing. */}
                <ItemCard.Description className="sm:hidden">
                    {t('controlCenter.search.compactPlaceholder', {
                        count: totalCount,
                    })}
                </ItemCard.Description>
                <ItemCard.Description className="hidden sm:block">
                    {t('controlCenter.search.placeholder', {
                        count: totalCount,
                    })}
                </ItemCard.Description>
            </ItemCard.Content>
            <ItemCard.Action className="hidden sm:block">
                {/* `Kbd.Content`, not `Kbd.Abbr keyValue="ctrl"` — the abbr
                    renders the macOS caret glyph, and this app runs on
                    Windows. */}
                <Kbd>
                    <Kbd.Content>{ctrl('shortcuts.controlShort')}</Kbd.Content>
                    <Kbd.Content>K</Kbd.Content>
                </Kbd>
            </ItemCard.Action>
        </ItemCard>
    );
};
